import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, describeError, type ApiError, type LoginMethods } from '../lib/api.ts';
import { T, tx, UI, LANGS, type Lang } from '../lib/i18n.ts';
import { safeNext } from '../lib/guest.ts';
import { describeOAuthError, stripOAuthError } from '../lib/oauth-errors.ts';
import { useApp } from '../state/app.tsx';
import { Icon } from '../components/Icon.tsx';
import { GoogleMark, TelegramButton } from '../components/ThirdParty.tsx';

/**
 * 登录：Telegram / Google 一键。
 *
 * **邮箱验证码登录 2026-10-29 删掉了**（老板定的）。它要我们自己维护一个发件邮箱，
 * 那个邮箱一被服务商限流或封号，所有人同时登不进来。
 * 本地开发另有一个「本地测试登录」，只在本地服务器上有（服务器说 devLogin 时才画）。
 *
 * 哪些按钮画得出来由**服务器**说（`GET /api/public/login-methods`）。
 * 前端写死的话，一个没配好的按钮点下去只会给出一句看不懂的报错。
 *
 * ════════════════════════════════════════════════════════
 * 没有密码，也永远不要钱包签名。
 * ════════════════════════════════════════════════════════
 * 这是个给链上交易者用的工具，而「让你用钱包签个名登录」正是
 * 盗币网站最常用的开场。所以我们把这句话写在登录页上：
 * **我们永远不会要你签名，也不会索要助记词或私钥。**
 * 说在前面，以后有人仿我们的站，用户能自己看出不对。
 *
 * 第一次登录会自动注册 —— 不做单独的注册流程。
 * 两个入口会引出「我到底注册过没有」这个问题，而答案对用户毫无意义。
 *
 * ── 两种进来的方式 ────────────────────────────────────
 *   `/login?next=…`   游客主动来登录（点了注册、或者点了保存）。
 *                     能回首页、能「先随便看看」、能换语言。登录完回 next。
 *   `expired`         用着用着会话过期了，整屏盖住当前页。
 *                     登录完原地继续，地址不变 —— 不给「随便看看」：
 *                     他不是来逛的，他的设置和数据就在下面等着。
 */

/** 只在本地服务器上出现的那一块（apps/api/src/server-local.ts 开的） */
const DEV = {
  title: T('本地测试登录', '本地測試登入', 'Local test sign-in'),
  note: T('只在本地开发服务器上有，线上没有这个入口。填任意邮箱就能进对应的测试账号。',
    '只在本地開發伺服器上有，線上沒有這個入口。填任意電郵就能進對應的測試帳戶。',
    'Only on the local development server; the live site has no such entry. Any email opens the matching test account.'),
  go: T('进入', '進入', 'Enter')
} as const;

const NO_METHOD = T('登录方式暂时都不可用，请稍后再试。', '登入方式暫時都不可用，請稍後再試。', 'No sign-in method is available right now; please try again later.');

/**
 * 这个错误是「来源不被服务器信任」吗。
 *
 * ════════════════════════════════════════════════════════
 * 为什么值得单独认出来
 * ════════════════════════════════════════════════════════
 * better-auth 为了防 CSRF 会比对 `Origin` 头，对不上就回 403 `Invalid origin`。
 * 而那五个字对用户毫无意义 —— 它长得像「你的账号有问题」，
 * 实际上是「这个网址没写进服务器的信任列表」。
 *
 * 第一次真跑这个登录页就撞上了：Vite 开发服务器绑 `127.0.0.1:5173`，
 * 而服务器信任列表里写的是 `localhost:5173`。同一类问题在扩展上
 * 已经出过两次，所以这里把它做成一条能照着改的提示。
 */
function looksLikeOriginProblem(e: ApiError): boolean {
  const msg = 'message' in e ? String(e.message) : '';
  return /invalid[_ ]?origin|origin.*(not.*trust|missing|null)/i.test(msg);
}

/**
 * 比 describeError 多两层：
 *   认得出来源问题，并说清该怎么改；
 *   **发码被拒的那几种原因按用户的语言说**（服务端的 `message` 是简体）。
 *
 * 后者只在这一页做，理由见 `lib/otp-errors.ts`：
 * 登录页是没登录的人唯一会碰到错误的地方，而整页英文配一句中文错误，
 * 那是一个英文用户对这个产品的第一印象。
 */
function explainError(e: ApiError, lang: Lang): string {
  if (looksLikeOriginProblem(e)) {
    const origin = typeof window === 'undefined' ? '(未知)' : window.location.origin;
    return `服务器不信任这个网址（${origin}），所以把登录请求当成跨站攻击挡掉了。`
      + `把 ${origin} 加进服务端的 WEB_ORIGINS，或者换用信任列表里的那个网址打开。`;
  }
  void lang;
  return describeError(e);
}

export function Login({ expired = false }: { expired?: boolean }): React.ReactElement {
  const app = useApp();
  const lang = app.lang;
  const loc = useLocation();
  const navigate = useNavigate();
  const next = safeNext(loc.search);
  /**
   * 第三方登录完回到哪一页。
   *
   * 会话过期那种情况下，登录框是**盖在当前页上**的，地址里没有 `next` ——
   * 用 next 的话会落回首页，而他的设置和数据就在刚才那一页等着。
   * 所以过期时回当前页，平时回 next。
   */
  const backTo = expired ? loc.pathname + stripOAuthError(loc.search) : next;
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyWhat, setBusyWhat] = useState<'social' | null>(null);
  /* 第三方登录失败会带着 `?error=` 回到这里。**第一屏就要说出来** ——
     不说的话他看到的是一个什么都没发生的登录页，会以为按钮坏了 */
  const [msg, setMsg] = useState<{ kind: 'info' | 'bad'; text: string } | null>(() => {
    const t = describeOAuthError(new URLSearchParams(loc.search).get('error'));
    return t === null ? null : { kind: 'bad', text: t };
  });

  /* 说完就把错误码从地址里拿掉，免得刷新一下又弹一遍 */
  useEffect(() => {
    if (new URLSearchParams(loc.search).has('error')) {
      navigate({ pathname: loc.pathname, search: stripOAuthError(loc.search) }, { replace: true });
    }
  }, [loc.pathname, loc.search, navigate]);
  /**
   * 服务器说哪些登录方式能用。null = 还没问到。
   *
   * 问不到的时候什么按钮都不画，只说一句「暂时不可用」。
   */
  const [methods, setMethods] = useState<LoginMethods | null>(null);

  useEffect(() => {
    document.title = `${tx(UI.signInTitle, lang)} · ${tx(UI.appName, lang)}`;
  }, [lang]);

  useEffect(() => {
    let alive = true;
    void api.get<LoginMethods>('/public/login-methods').then(r => {
      if (alive && r.ok) setMethods(r.data);
    });
    return () => { alive = false; };
  }, []);

  /**
   * Google 登录。
   *
   * better-auth 的 `/sign-in/social` 不会自己跳转，它返回一个要去的地址 ——
   * 由我们整页跳过去。用 `window.location.href` 而不是 react-router：
   * 目标是第三方域名，路由器管不了它。
   *
   * `callbackURL` 必须是**绝对地址**。相对路径会被 better-auth 按它自己的
   * baseURL 解析，而本地开发时那是 :4000（接口），不是 :5173（网页）——
   * 表现是登录成功之后落在一个 JSON 页面上。
   */
  const signInGoogle = async (): Promise<void> => {
    setBusy(true);
    setBusyWhat('social');
    setMsg(null);
    const r = await api.post<{ url?: string; redirect?: boolean }>('/auth/sign-in/social', {
      provider: 'google',
      callbackURL: window.location.origin + backTo,
      /* 出错也要回到登录页（带着 ?error=，上面会翻成人话），
         而不是停在接口的错误 JSON 上。next 要带着 —— 重试成功之后还要回原处 */
      errorCallbackURL: window.location.origin + '/login?next=' + encodeURIComponent(backTo)
    });
    if (!r.ok || typeof r.data?.url !== 'string' || r.data.url === '') {
      setBusy(false);
      setBusyWhat(null);
      setMsg({ kind: 'bad', text: r.ok ? 'Google 登录暂时用不了，请用 Telegram' : explainError(r.error, lang) });
      return;
    }
    window.location.href = r.data.url;
  };

  /**
   * Telegram 授权完成之后的那一下。
   *
   * 把组件回传的那个对象**原样**发给服务端 —— 一个字段都不能挑：
   * 签名是用「收到的全部字段」算出来的，少一个就验不过。
   */
  const signInTelegram = (payload: Record<string, unknown>): void => {
    setBusy(true);
    setBusyWhat('social');
    setMsg(null);
    void api.post('/auth/telegram/sign-in', payload).then(async r => {
      if (!r.ok) {
        setBusy(false);
        setBusyWhat(null);
        setMsg({ kind: 'bad', text: explainError(r.error, lang) });
        return;
      }
      /* 登录成功后把全部数据重新拉一遍。这之后本组件会被卸载，所以不再碰它的状态 */
      await app.onSignedIn();
    });
  };


  /** 本地测试登录（只有本地服务器开着它） */
  const devSignIn = async (): Promise<void> => {
    const addr = email.trim().toLowerCase();
    if (!addr.includes('@')) { setMsg({ kind: 'bad', text: tx(UI.badEmail, lang) }); return; }
    setBusy(true);
    setMsg(null);
    const r = await api.post('/dev/sign-in', { email: addr });
    if (!r.ok) { setBusy(false); setMsg({ kind: 'bad', text: explainError(r.error, lang) }); return; }
    await app.onSignedIn();
  };

  const none = methods !== null && !methods.google && methods.telegram === null && methods.devLogin !== true;

  return (
    <div className="authwrap">
      <div className="authbox">
        {!expired ? (
          <div className="auth-top">
            <Link to="/" className="btn sm ghost">
              ← {tx(UI.backHome, lang)}
            </Link>
            {/* 游客的语言只存在这台电脑上，登录之后会带进新账号 */}
            <span className="seg sm" role="group" aria-label={tx(UI.language, lang)}>
              {LANGS.map(([id, name]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={lang === id}
                  lang={id === 'en' ? 'en' : id === 'zh-HK' ? 'zh-Hant-HK' : 'zh-Hans'}
                  onClick={() => void app.setLang(id)}
                >
                  {id === 'zh-CN' ? '简' : id === 'zh-HK' ? '繁' : 'EN'}
                  <span className="sr-only">{name}</span>
                </button>
              ))}
            </span>
          </div>
        ) : null}

        <div className="brand">
          <Icon name="shield" style="line" />
          {tx(UI.appName, lang)}
        </div>
        <h1>{expired ? tx(UI.signIn, lang) : tx(UI.signInTitle, lang)}</h1>

        {expired ? (
          <div className="banner warn">
            <span className="ic"><Icon name="alert" /></span>
            <span>{tx(UI.sessionExpired, lang)}</span>
          </div>
        ) : null}

        {methods?.telegram ? (
          <TelegramButton
            bot={methods.telegram.botUsername}
            lang={lang}
            onAuth={signInTelegram}
          />
        ) : null}
        {methods?.google ? (
          <button
            type="button"
            className="btn block social google"
            disabled={busy}
            onClick={() => void signInGoogle()}
          >
            <GoogleMark />
            {busy && busyWhat === 'social' ? tx(UI.socialBusy, lang) : tx(UI.signInGoogle, lang)}
          </button>
        ) : null}
        {none ? <div className="banner warn"><span>{tx(NO_METHOD, lang)}</span></div> : null}

        {methods?.devLogin === true ? (
          <div className="fld" data-dev-login>
            <label htmlFor="email">{tx(DEV.title, lang)}</label>
            <input
              id="email"
              className="inp"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              disabled={busy}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void devSignIn(); }}
            />
            <span className="foot">{tx(DEV.note, lang)}</span>
            <button type="button" className="btn block" disabled={busy} onClick={() => void devSignIn()}>
              {tx(DEV.go, lang)}
            </button>
          </div>
        ) : null}

        {msg ? (
          <div className={msg.kind === 'bad' ? 'banner urgent' : 'banner info'} role={msg.kind === 'bad' ? 'alert' : 'status'}>
            <span className="ic"><Icon name={msg.kind === 'bad' ? 'alert' : 'info'} /></span>
            <span>{msg.text}</span>
          </div>
        ) : null}

        <div className="foot">
          <div>{tx(UI.signInHint, lang)}</div>
          <div style={{ marginTop: 8 }}>
            <b>{tx(UI.noWalletSign, lang)}</b>
          </div>
          {!expired ? (
            <div style={{ marginTop: 12 }}>
              <Link to={next}>{tx(UI.justLook, lang)} →</Link>
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              {/* 整页刷新回首页：会话已经没了，内存里那份账号数据不该再留着 */}
              <a href="/">{tx(UI.backHome, lang)}</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
